import React from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Button, { ButtonProps } from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha, useTheme } from '@mui/material/styles'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import CancelIcon from '@mui/icons-material/Cancel'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import AddIcon from '@mui/icons-material/Add'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PricingInfoButton from 'components/PricingInfoButton'
import update from 'immutability-helper'
import { timer } from 'rxjs'
import { useAPI } from 'contexts/APIProvider'
import AvailabilityCheck from 'logic/AvailabilityCheck'
import { LeadAvailabilityCheckForm, EstimateCost, EstimateCostForm } from 'types/interfaces'
import LeadAvailabilityCheckFormError from 'errors/LeadAvailabilityCheckFormError'
import ErrorList from 'components/ErrorList'
import LeadAvailabilityCheckFields from './LeadAvailabilityCheckFields'
import LeadDetailContext from '../context'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { formatCurrencyAmount } from 'logic/helpers'

function sortChecks(checks: AvailabilityCheck[]): AvailabilityCheck[] {
  return [...checks].sort((a, b) => {
    const priority = (c: AvailabilityCheck) => {
      if (c.is_expired) return 3
      if (c.isNurseFound && !c.isBookingConfirmed) return 0
      if (!c.isNurseFound && !c.isBookingConfirmed) return 1
      return 2
    }
    const pa = priority(a)
    const pb = priority(b)
    if (pa !== pb) return pa - pb
    return b.created_at.toMillis() - a.created_at.toMillis()
  })
}

function StatusIcon({ check, fontSize = 'medium' }: { check: AvailabilityCheck; fontSize?: 'small' | 'medium' | 'large' }) {
  if (check.is_expired) return <CancelIcon fontSize={fontSize} color="disabled" />
  if (check.isBookingConfirmed) return <EventAvailableIcon fontSize={fontSize} color="primary" />
  if (check.isNurseFound) return <CheckCircleIcon fontSize={fontSize} color="success" />
  return <HourglassEmptyIcon fontSize={fontSize} color="primary" sx={{
    animation: "spin 2s linear infinite",
    "@keyframes spin": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" },
    },
  }} />
}

function AvailabilityCheckChip({ check, onClick, cost }: { check: AvailabilityCheck; onClick: () => void; cost?: EstimateCost }) {
  const theme = useTheme()
  const needsAction = check.isNurseFound && !check.isBookingConfirmed
  const label = cost ? `${check.dateLabel()} · ${formatCurrencyAmount(cost.median_total, 'GBP')}` : check.dateLabel()
  return (
    <Chip
      icon={<StatusIcon check={check} fontSize="small" />}
      label={label}
      color={check.statusColor === 'default' ? undefined : check.statusColor as any}
      variant="outlined"
      onClick={onClick}
      sx={{
        px: 1,
        ...(needsAction && {
          animation: 'chipPulse 1.5s ease-in-out infinite',
          '@keyframes chipPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.6)}` },
            '50%': { boxShadow: `0 0 0 6px ${alpha(theme.palette.success.main, 0)}` },
          },
        }),
      }}
    />
  )
}

function AvailabilityCheckItem({ check, isSelected, onClick, cost }: { check: AvailabilityCheck; isSelected: boolean; onClick: () => void; cost?: EstimateCost }) {
  const theme = useTheme()
  const needsAction = check.isNurseFound && !check.isBookingConfirmed
  return (
    <Paper
      elevation={isSelected ? 3 : 1}
      sx={{
        p: 2,
        cursor: 'pointer',
        border: isSelected ? 2 : 1,
        borderStyle: 'solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        ...(needsAction && {
          bgcolor: alpha(theme.palette.success.main, 0.08),
          borderColor: isSelected ? 'primary.main' : alpha(theme.palette.success.main, 0.4),
          animation: 'paperPulse 1.5s ease-in-out infinite',
          '@keyframes paperPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.5)}` },
            '50%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.success.main, 0)}` },
          },
        }),
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, alignItems: 'center' }}>
        <StatusIcon check={check} fontSize="large" />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" fontWeight={needsAction ? 'bold' : undefined}>
            {check.dateLabel()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {check.timeLabel()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {check.role}
          </Typography>
          {cost && (
            <Typography variant="caption" color="text.secondary">
              Est. {formatCurrencyAmount(cost.median_total, 'GBP')}
            </Typography>
          )}
        </Box>
        <Chip label={check.statusLabel} color={check.statusColor as any} size="small" />
      </Box>
    </Paper>
  )
}

interface AddAvailabilityCheckDialogProps {
  open: boolean
  onClose: () => void
  form: LeadAvailabilityCheckForm
  formError: LeadAvailabilityCheckFormError | undefined
  updateForm: (name: string, value: any) => void
  loading: boolean
  onSubmit: () => void
}

function AddAvailabilityCheckDialog({ open, onClose, form, formError, updateForm, loading, onSubmit }: AddAvailabilityCheckDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Another Date</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add another date when you need a nurse.
        </Typography>
        {formError?.schema?.length ? (
          <Alert severity="error" sx={{ mb: 2 }}><ErrorList errors={formError.schema} /></Alert>
        ) : null}
        <Box sx={{ mt: 1 }}>
          <LeadAvailabilityCheckFields form={form} error={formError} updateForm={updateForm} hidePostcode />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSubmit} disabled={loading}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              Adding...
            </Box>
          ) : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}


interface AddDateButtonProps extends ButtonProps {
  onSuccess: () => void
}

function AddDateButton({ onSuccess, ...buttonProps }: AddDateButtonProps) {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const { lead, availabilityCheck } = React.useContext(LeadDetailContext)
  const [open, setOpen] = React.useState(false)
  const [form, setForm] = React.useState<LeadAvailabilityCheckForm>({})
  const [formError, setFormError] = React.useState<LeadAvailabilityCheckFormError>()
  const [loading, setLoading] = React.useState(false)

  const updateForm = React.useCallback((name: string, value: any) => {
    setForm(prev => update(prev, { [name]: { $set: value } }))
    setFormError(prev => prev ? update(prev, { [name]: { $set: [] } }) : prev)
  }, [])

  const handleAddCheck = React.useCallback(async () => {
    if (!lead) return
    setLoading(true)
    setFormError(undefined)
    try {
      await api.createLeadAvailabilityCheck({
        ...form,
        lead_id: lead.id,
        postcode: availabilityCheck?.postcode,
        lat: availabilityCheck?.lat,
        lng: availabilityCheck?.lng,
      })
      setOpen(false)
      setForm({})
      await onSuccess()
      showAlert('success', 'Availability check added successfully')
    } catch (e: any) {
      if (e instanceof LeadAvailabilityCheckFormError) {
        setFormError(e)
      } else {
        console.error('unhandled exception', e)
        showAlert('error', 'An unexpected error occurred.')
      }
    } finally {
      setLoading(false)
    }
  }, [lead, api, form, availabilityCheck, showAlert, onSuccess])

  return (
    <>
      <Button {...buttonProps} onClick={() => setOpen(true)} />
      <AddAvailabilityCheckDialog
        open={open}
        onClose={() => setOpen(false)}
        form={form}
        formError={formError}
        updateForm={updateForm}
        loading={loading}
        onSubmit={handleAddCheck}
      />
    </>
  )
}

const LeadAvailabilityChecksPanel: React.FC = () => {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const { lead, availabilityCheck, paygradeStats, setAvailabilityCheck, setGtmCheckStats } = React.useContext(LeadDetailContext)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [checks, setChecks] = React.useState<AvailabilityCheck[]>([])
  const [collapsed, setCollapsed] = React.useState(false)
  const [selectedId, setSelectedId] = React.useState<number>()
  const [estimatedCosts, setEstimatedCosts] = React.useState<Map<number, EstimateCost>>(new Map())
  const estimatedCostsRef = React.useRef<Map<number, EstimateCost>>(new Map())

  const fetchEstimatedCostForCheck = React.useCallback(async (check: AvailabilityCheck) => {
    if (!paygradeStats) return
    const tz = 'Europe/London'
    const specialistRequired = (
      check.sedation_required || 
      check.radiography_required ||
      check.implant_required  || 
      check.orthodontics_required
    )
    const form: EstimateCostForm = {
      date: check.start_at.setZone(tz).toFormat('yyyy-MM-dd'),
      start_time: check.start_at.setZone(tz).toFormat('HH:mm'),
      end_time: check.end_at.setZone(tz).toFormat('HH:mm'),
      lunch_break: check.lunch_break.as('minutes'),
      headcount: 1,
      role: check.role,
      specialist_required: specialistRequired,
      min_rate: paygradeStats.min_rate,
      max_rate: paygradeStats.max_rate,
      median_rate: paygradeStats.median_rate,
    }
    try {
      const cost = await api.getEstimatedCost(form)
      estimatedCostsRef.current = new Map(estimatedCostsRef.current).set(check.id, cost)
      setEstimatedCosts(prev => new Map(prev).set(check.id, cost))
    } catch (e) {
      console.error('Failed to load estimated cost for check', check.id, e)
    }
  }, [paygradeStats, api])

  const fetchChecks = React.useCallback(async () => {
    if (!lead?.id) return
    try {
      const results = await api.listAvailabilityChecks({ lead_id: lead.id, sideloads: ['recommended_nurse', 'num_results'] })
      const sorted = sortChecks(results)
      setChecks(sorted)

      // update GTM check stats in context
      const activeChecks = sorted.filter(c => !c.is_expired)
      const numTotal = activeChecks.length
      const numNurseFound = activeChecks.filter(c => c.isNurseFound || c.isBookingConfirmed).length
      const pctNurseFound = numTotal > 0 ? Math.round((numNurseFound / numTotal) * 100) : 0
      setGtmCheckStats({ numTotal, numNurseFound, pctNurseFound })

      // fetch estimated costs for each check (if it's not already in the map)
      const newChecks = sorted.filter(c => !estimatedCostsRef.current.has(c.id))
      await Promise.all(newChecks.map(c => fetchEstimatedCostForCheck(c)))
      return sorted
    } catch (e) {
      console.error('Failed to fetch availability checks', e)
      showAlert('error', 'An unexpected error occurred while loading availability checks.')
    }
  }, [api, fetchEstimatedCostForCheck, lead?.id, showAlert, setGtmCheckStats])

  // poll for checks every 5 seconds
  React.useEffect(() => {
    if (!lead) return
    const subscription = timer(0, 5000).subscribe(fetchChecks)
    return () => subscription.unsubscribe()
  }, [lead, fetchChecks])

  // if selectedId is set, set availability check to the selected check
  // if no selectedId is set, set availability check to the first check (following sort order from sortChecks)
  React.useEffect(() => {
    if (selectedId) {
      const selected = checks.find((c) => c.id === selectedId)
      if (selected) setAvailabilityCheck(selected)
    } else if (checks.length > 0) {
      setAvailabilityCheck(checks[0])
    }
  }, [selectedId, checks, setAvailabilityCheck])

  // calculate the subtotal of estimated costs for all checks that are not expired
  const activeChecks = React.useMemo(() => checks.filter(c => !c.is_expired), [checks])
  const allCostsLoaded = React.useMemo(
    () => activeChecks.length > 0 && activeChecks.every(c => estimatedCosts.has(c.id)),
    [activeChecks, estimatedCosts],
  )
  const subtotal = React.useMemo(
    () => activeChecks.reduce((sum, c) => sum + (estimatedCosts.get(c.id)?.median_total ?? 0), 0),
    [activeChecks, estimatedCosts],
  )

  // generate mobile text for the collapsed panel
  const collapsedText = React.useMemo(() => {
    if (checks.length === 0) return 'No checks added yet'
    const nurseFound = checks.find(c => c.isNurseFound && !c.isBookingConfirmed)
    if (nurseFound) return `Nurse found for ${nurseFound.dateLabel()}`
    const confirmed = checks.filter(c => c.isBookingConfirmed)
    if (confirmed.length > 0) return `${confirmed.length} booking${confirmed.length !== 1 ? 's' : ''} confirmed`
    const pending = checks.filter(c => !c.is_expired)
    return `${pending.length} check${pending.length !== 1 ? 's' : ''} pending`
  }, [checks])

  if (checks.length === 0) {
    if (isMobile) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">No checks yet.</Typography>
          <AddDateButton onSuccess={fetchChecks} color="primary" variant="outlined" size="small" endIcon={<AddIcon />}>
            Add Date
          </AddDateButton>
          <PricingInfoButton paygradeStats={paygradeStats} size="small" />
        </Box>
      )
    }
    return (
      <>
        <Typography variant="h6" sx={{ mb: 1 }}>Availability Checks</Typography>
        <Typography variant="body2" color="text.secondary">
          No availability checks yet. Add one to start finding a nurse.
        </Typography>
      </>
    )
  }

  if (isMobile) {
    return (
      <>
        <Paper sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setCollapsed(c => !c)}
          >
            {collapsed ? (
              <Typography variant="body2" color="text.secondary">{collapsedText}</Typography>
            ) : (
              <Typography variant="h6">Availability Checks</Typography>
            )}
            <IconButton size="small">
              {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Box>
          {!collapsed && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {checks.map(check => (
                  <AvailabilityCheckChip key={check.id} check={check} cost={estimatedCosts.get(check.id)} onClick={() => setSelectedId(check.id)} />
                ))}
              </Box>
              <AddDateButton onSuccess={fetchChecks} color="primary" variant="outlined" size="small" endIcon={<AddIcon />}>
                Add Date
              </AddDateButton>
              <PricingInfoButton paygradeStats={paygradeStats} size="small" />
            </>
          )}
        </Paper>
      </>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Availability Checks</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {checks.map(check => (
          <AvailabilityCheckItem
            key={check.id}
            check={check}
            isSelected={availabilityCheck?.id === check.id}
            cost={estimatedCosts.get(check.id)}
            onClick={() => setSelectedId(check.id)}
          />
        ))}
      </Box>
      {allCostsLoaded && subtotal > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">Estimated subtotal</Typography>
          <Typography variant="body2" fontWeight="bold">{formatCurrencyAmount(subtotal, 'GBP')}</Typography>
        </Box>
      )}
      <AddDateButton onSuccess={fetchChecks} variant="outlined" fullWidth sx={{ mt: 1 }}>
        Add Another Date
      </AddDateButton>
      <PricingInfoButton paygradeStats={paygradeStats} />
    </Box>
  )
}

export default LeadAvailabilityChecksPanel
